import React from 'react'
import { NextImage } from '@/components/NextImage'
import { BaseFormOption } from '@/actors/AuthActor/types'
import { DEFAULT_HEADER_IMAGE_HEIGHT, DEFAULT_HEADER_IMAGE_WIDTH } from '@/actors/AuthActor/theme/resolveAuthTheme'
import { useBrandAssets } from '@/hooks/cinematics/useBrandAssets'

import classNames from 'classnames'
import classes from './style.module.scss'

interface FormLayoutProps {
  children: React.ReactNode
  compact?: boolean
  form?: BaseFormOption
  /** Resolved from the theme: scene-wide token, or the form's own override. */
  headerImage?: string
  headerImageWidth?: number
  headerImageHeight?: number
}


export const FormLayout: React.FC<FormLayoutProps> = ({ children, ...props }) => {
  const {
    form,
    compact = false,
    headerImage,
    headerImageWidth = DEFAULT_HEADER_IMAGE_WIDTH,
    headerImageHeight = DEFAULT_HEADER_IMAGE_HEIGHT
  } = props
  const { logo: logoUrl } = useBrandAssets()
  const hasForm = !!form && Object.keys(form).length > 0
  const formType = (form as { type?: string } | undefined)?.type
  // Nothing renders a logo in compact mode. Note that `compact` alone does not
  // identify an embedded panel — MyTeamActor mounts the actor with only
  // `redirectOnAuthenticated={false}` — so keeping a scene-wide
  // `theme.headerImage` out of flows that never had one is AuthActor's job: it
  // gates the prop on `isEmbedded` before passing it here.
  // Otherwise an explicit header image renders on any form type — the ambiance
  // logo stays limited to standard forms, which is what the
  // container/callback/redirect layouts were built around.
  const shouldShowImage = !compact && (!!headerImage || !hasForm || formType === 'standard')

  return (
    <div
      className={classNames({
        [classes.form]: true,
        [classes['form--compact']]: compact
      })}
    >
      {shouldShowImage && (
        <NextImage
          className={classes.logo}
          width={headerImageWidth}
          height={headerImageHeight}
          src={headerImage || logoUrl || ''}
          alt="Logo"
        />
      )}
      <div className={classes.content}>
        {children}
      </div>
    </div>
  )
}
